import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BlockIcon from '@mui/icons-material/Block';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ChatIcon from '@mui/icons-material/Chat';
import { useLocation, useNavigate } from 'react-router-dom';

import { theme } from '../../theme';
import { useAuth } from '../../context/AuthContext';
import ChatBubble from '../messaging/ChatBubble';

const drawerWidth = 80;
const mobileDrawerWidth = 240;

const SuperAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout, user } = useAuth();

  const navItems = useMemo(
    () => {
      const items = [
        { label: 'Dashboard', icon: <DashboardIcon />, to: '/superadmin/overview' },
        { label: 'Professionnels', icon: <PersonIcon />, to: '/superadmin/professionals' },
        { label: 'Salons', icon: <StoreIcon />, to: '/superadmin/salons' },
        { label: 'Clients', icon: <PeopleIcon />, to: '/superadmin/clients' },
        { label: 'Messagerie', icon: <ChatIcon />, to: '/superadmin/messaging' },
        { label: 'Bans', icon: <BlockIcon />, to: '/superadmin/bans' },
        { label: 'Absences', icon: <EventBusyIcon />, to: '/superadmin/no-shows' },
      ];

      items.push({ label: 'Utilisateurs', icon: <ManageAccountsIcon />, to: '/superadmin/users' });
      items.push({ label: 'Abonnements', icon: <CreditCardIcon />, to: '/superadmin/subscriptions' });

      return items;
    },
    []
  );

  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 900,
            fontSize: '1.2rem',
            color: '#fff',
          }}
        >
          G
        </Box>
      </Box>
      <Divider sx={{ borderColor: theme.colors.border.light }} />
      <List sx={{ px: 1.5, py: 2, flex: 1 }}>
        {navItems.map((item) => {
          const selected = location.pathname.startsWith(item.to);
          return (
            <Tooltip key={item.to} title={item.label} placement="right">
              <ListItemButton
                selected={selected}
                onClick={() => {
                  navigate(item.to);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  minHeight: 48,
                  justifyContent: { xs: 'flex-start', md: 'center' },
                  px: { xs: 2, md: 0 },
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    backgroundColor: theme.colors.primary[50],
                    '&:hover': { backgroundColor: theme.colors.primary[100] },
                  },
                  '&:hover': {
                    backgroundColor: theme.colors.grey[100],
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: { xs: 40, md: 0 },
                    justifyContent: 'center',
                    color: selected ? theme.colors.primary[600] : theme.colors.text.secondary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiTypography-root': {
                      fontWeight: selected ? 700 : 500,
                      fontSize: '0.9rem',
                      color: selected ? theme.colors.primary[600] : theme.colors.text.primary,
                    },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
      <Divider sx={{ borderColor: theme.colors.border.light }} />
      <List sx={{ px: 1.5, py: 2 }}>
        <Tooltip title="Logout" placement="right">
          <ListItemButton
            onClick={async () => {
              try {
                await logout();
                // Redirection handled by ProtectedRoute
              } catch (error) {
                console.error('Logout failed:', error);
              }
              setMobileOpen(false);
            }}
            sx={{
              borderRadius: 2,
              minHeight: 48,
              justifyContent: { xs: 'flex-start', md: 'center' },
              px: { xs: 2, md: 0 },
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.colors.error[50],
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 40, md: 0 }, justifyContent: 'center', color: theme.colors.text.secondary }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Déconnexion"
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiTypography-root': {
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: theme.colors.text.primary,
                },
              }}
            />
          </ListItemButton>
        </Tooltip>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.colors.secondary[50], maxWidth: '100vw', overflowX: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          backgroundColor: '#fff',
          borderBottom: `1px solid ${theme.colors.border.light}`,
          color: theme.colors.text.primary,
        }}
      >
        <Toolbar sx={{ minHeight: 70, px: { xs: 2, md: 3 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.25rem' }, color: theme.colors.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Admin Dashboard
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton sx={{ color: theme.colors.text.secondary, mr: 1 }}>
            <NotificationsIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              {user?.first_name?.[0] || 'A'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.2, color: theme.colors.text.primary }}>
                {user?.first_name || 'Admin'} {user?.last_name || 'User'}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary, lineHeight: 1.2 }}>
                {user?.email || 'admin@glow.com'}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: mobileDrawerWidth, backgroundColor: '#fff' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${theme.colors.border.light}`,
              backgroundColor: '#fff',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          pt: { xs: 11, md: 12 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          overflowX: 'hidden'
        }}
      >
        {children}
        <ChatBubble />
      </Box>
    </Box >
  );
};

export default SuperAdminLayout;
